<script setup lang="ts">
import GwTooltipNew from '@/components/lib/GwTooltip/GwTooltipNew.vue'
import { t } from '@/locales/i18n'
import { computed } from 'vue'

export type GwColorNodeProps = {
  variant?: 'single_row' | 'double_row'
} & (
  | {
      multiValue?: false
      colorHex: string
    }
  | {
      multiValue: true
      colorHex: string[]
    }
)

const props = withDefaults(defineProps<GwColorNodeProps>(), {
  variant: 'single_row',
  multiValue: false,
})

const emit = defineEmits<{
  'update:color': [value: string] //
  'update:colors': [values: string[]]
}>()

const colors = [
  '#F25C57',
  '#E97852',
  '#E5B466',
  '#EBCB44',
  '#B1D14D',
  '#569976',
  '#5ABEB9',
  '#8DCAC7',
  '#3761BE',
  '#6964EC',
  '#A05BCD',
  '#EF99EB',
]

const colorValues = computed(
  () => (props.multiValue ? props.colorHex : [props.colorHex]) as string[],
)

const isSelected = (color: string) => colorValues.value.includes(color)

const handleUpdate = (color: string) => {
  if (props.multiValue) {
    emit(
      'update:colors',
      isSelected(color)
        ? colorValues.value.filter(c => c !== color)
        : [...colorValues.value, color],
    )
  } else {
    emit('update:color', isSelected(color) ? '' : color)
  }
}
</script>

<template>
  <div
    class="gw_color_nodes"
    :class="`gw_color_nodes_${variant}`"
    style="--icon-size: 2rem"
  >
    <div
      v-for="color in colors"
      :key="color"
      class="gw_color_node"
      @click="handleUpdate(color)"
    >
      <GwTooltipNew
        v-if="colorValues.includes(color)"
        :tooltip="t('common.remove_tag')"
        placement="top"
      >
        <template #opener="{ listeners }">
          <i
            class="mdi mdi-circle-outline position-absolute z-index_2 gw_color_node_icon"
            v-on="listeners"
          />
        </template>
      </GwTooltipNew>
      <i
        class="mdi mdi-circle gw_color_node_icon"
        :class="!colorValues.includes(color) && 'gw_color_node_icon_base'"
        :style="{ color }"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.gw_color_nodes {
  --icon-size: 2rem;
  --icon-gap: 0.25rem;
  line-height: var(--icon-size);
  gap: var(--icon-gap);
  &_single_row {
    display: flex;
    justify-content: flex-start;
  }
  &_double_row {
    display: grid;
    grid-template-columns: repeat(6, 2fr);
  }
}

.gw_color_node {
  cursor: pointer;
  height: var(--icon-size);
  width: var(--icon-size);
  &_icon {
    font-size: var(--icon-size);
    &_base {
      opacity: 0.7;
      &:hover {
        opacity: 1 !important;
      }
    }
  }
}
@media (max-width: 1024px) {
  .gw_color_nodes {
    --icon-size: 2.5rem; // bigger icons for mobile so it's easier to tap
    display: flex;
    justify-content: stretch;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 0.25rem;
    &_double_row {
      margin-top: 0.5rem;
      --icon-size: 3rem !important;
    }
  }
}
</style>
